import { checkSync } from "recheck";

import type { NodeSearch, PathTerm } from "../search.ts";
import type { Node, NodeOf } from "../types.ts";
import type { parse as innerParse } from "./parser.js";

export { parse as dslParse } from "./parser.esm.js";

const FALLBACK_KEYTERM = { mode: "i", invert: false, keyword: "" };

type NodeMapping<G extends object, K extends keyof G> = {
  /** Values used to compare against `term` or `type:term` searches */
  keys?: (node: Node<G, K>) => string[];
  /** Mapping used to evaluate `type:attribute:term` searches */
  attributes?: Record<string, (node: Node<G, K>) => string[]>;
};

type NodePredicate<G extends object> = (node: NodeOf<G>) => boolean;

export type DslParseResult<G extends object> = NodeSearch<G>[] | ParseWarning;

export type ParseWarning = { warning: string };
export const isParseWarning = <G extends object>(
  value: DslParseResult<G>
): value is ParseWarning => "warning" in value;

/** Mapping of possible types and their associated attributes
 *
 * If a type is in the mapping with an empty attribute list, it may be used for
 * a type query with no allowed attributes. If a type is not in the mapping,
 * it may not be used for a type query.
 */
type DslTypes = Record<string, string[]>;

export type DslParser = typeof innerParse;
export type NodeTerm = ReturnType<
  typeof innerParse
>[number]["path"]["nodes"][number];

export type DslMapping<G extends object> = {
  [K in keyof G]?: NodeMapping<G, K>;
};
export type DslAliases = Record<string, (ast: NodeTerm) => NodeTerm>;

const someValue = (
  values: any[],
  lower: boolean,
  predicate: (value: string) => boolean
) => {
  for (const v of values) {
    if (v !== undefined) {
      const value = lower ? String(v).toLowerCase() : String(v);
      if (predicate(value)) return true;
    }
  }
  return false;
};

// Implements the "termMatch" portion of the DSL
const predicate =
  <G extends object>(args: {
    mapping: DslMapping<G>;
    aliases: DslAliases;
    types: DslTypes;
  }) =>
  (nodeTerm: NodeTerm): PathTerm<G> => {
    const { mapping, aliases, types } = args;
    const aliased =
      nodeTerm.type && nodeTerm.type in aliases
        ? aliases[nodeTerm.type](nodeTerm)
        : nodeTerm;
    const { mode, properties, type } = aliased;

    if (type && !(type in types)) {
      throw new Error(
        `Type term '${type}:' is invalid. Valid types are:
${Object.keys(types).join(", ")}.`
      );
    }

    const abortOnTypeMismatch = mode === "is";
    const typeMap = mapping[type as keyof G];
    const keywords: string[] = [];
    const predicates: NodePredicate<G>[] = [];
    const attributesFor = typeMap?.attributes;
    const validAttributes = new Set([
      ...Object.keys(attributesFor ?? {}),
      ...(types[type ?? ""] ?? []),
      "key",
    ]);
    for (const { attribute, keyterm } of properties) {
      const { mode, invert, keyword } = keyterm ?? FALLBACK_KEYTERM;
      const exact = mode === "e";
      const compareKeyword = mode === "i" ? keyword.toLowerCase() : keyword;
      if (type && attribute && !validAttributes.has(attribute)) {
        throw new Error(
          `Attribute '${attribute}' is invalid for type '${type}'. Valid attributes are: ${[
            ...validAttributes.keys(),
          ].join(", ")}.${
            keyterm?.keyword
              ? `\nDid you mean '${type}:key:${attribute}:${keyword}'?`
              : ""
          }`
        );
      }
      if (keyterm && !invert) keywords.push(keyterm.keyword);
      const nodeValues =
        attribute && attribute !== "key"
          ? (attributesFor?.[attribute] ??
            ((node: NodeOf<G>) => [
              (node.data as Record<string, any>)[attribute],
            ]))
          : (typeMap?.keys ?? ((node: NodeOf<G>) => [node.key]));
      let regex: RegExp | undefined;
      if (mode === "r") {
        try {
          regex = new RegExp(keyword);
        } catch {
          throw new Error(`Invalid regex pattern: /${keyword}/`);
        }
        const redosDiagnosis = checkSync(keyword, "");
        if (redosDiagnosis.status === "vulnerable") {
          throw new Error(
            `Regex pattern /${keyword}/ is vulnerable to ReDoS (${redosDiagnosis.complexity.summary}). Please simplify the pattern.`
          );
        }
      }
      predicates.push((node) => {
        // TODO: memoize values
        const match = someValue(nodeValues(node), mode === "i", (v) =>
          keyterm
            ? regex
              ? regex.test(v)
              : exact
                ? v === keyword
                : v.includes(compareKeyword)
            : true
        );
        return match !== invert;
      });
    }
    const predicate: NodePredicate<G> = (node) => {
      if (type && node.type !== type) return false;
      return predicates.every((p) => p(node));
    };
    return {
      abortOnTypeMismatch,
      keywords,
      predicate,
      keytype: type,
    };
  };

/** Parses a graph search DSL
 *
 * DSL:
 * Produces a preset that finds all grant nodes that can reach:
 *   term - A node containing term
 *   !term - FA node that does not contain term
 *   "term" - A node exactly matching term
 *   !"term" - A node that does not exactly match term
 *   type:{termMatch} - A node that matches type with term match obeying above rules
 *   type: - A node that matches type
 *   type:attribute:{termMatch} - A node that type type and whose attribute matches term-match rules
 *   {via}->{node} - A node matching {node} term, reachable via {via} term
 * Can also construct a preset that finds all grant nodes that can not reach matches,
 * using ^{...}
 *
 * For each node, term will match the node key, unless the node type appears
 * in the `mapping` parameter, in which case the node's string value will be
 * determined from the mapping value for that node's type.
 *
 * Aliases will string substitute term matches.
 *
 * Example:
 *
 *    ^usage:type:!"unused"->permission:
 *
 * finds all grants that can not reach a used or unknown permission (this is the expression
 * for the "Unused Grants" preset) when the assessment DSL mapping is used.
 *
 */
export const parse =
  <G extends object>(args: {
    mapping: DslMapping<G>;
    aliases: DslAliases;
    // parser is added as argument to prevent cjs / esm issues on frontend
    parser: DslParser;
    types: DslTypes;
  }) =>
  (query: string): DslParseResult<G> => {
    // TODO: fix aliases
    try {
      const ast = args.parser(query, { grammarSource: "input" });
      return ast.map((term) => {
        const path = term.path.nodes.map((node) => predicate(args)(node));
        return {
          exclude: term.exclude,
          direction: term.path.direction,
          pathTerms: path,
          term: term.term,
        };
      });
    } catch (error: any) {
      const warning =
        "format" in error
          ? error.format([{ source: "input", text: query }])
          : String(error);
      return { warning };
    }
  };

export const check = <G extends object>(
  value: DslParseResult<G>
): NodeSearch<G>[] => {
  if (isParseWarning(value)) throw new Error(value.warning);
  return value;
};
